import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PortfolioConstituentBreakdownResponse } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { openPopoutWindow } from '~/utils/popoutUtils';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { exportWidgetToPDF } from '../common/pdfExportUtils';

export type AssetBreakdownWidgetProps = {
  data: PortfolioConstituentBreakdownResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
  scenario?: string;
  horizonDays?: number;
  portfolioId?: string | null;
  portfolioName?: string | null;
};

export const AssetBreakdownWidget: React.FC<AssetBreakdownWidgetProps> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Forward Returns Breakdown',
  snapshotId,
  scenario = 'proposed',
  horizonDays,
  portfolioId,
  portfolioName,
}) => {
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const [hoveredAsset, setHoveredAsset] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const chartRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleMouseEnter = useCallback((key: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredAsset(key);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Delay clearing to allow entering another area first
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredAsset(null);
    }, 100);
  }, []);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality using dedicated endpoint
  const handlePopout = useCallback(() => {
    openPopoutWindow({
      widgetType: 'asset-breakdown',
      snapshotId: snapshotId || undefined,
      scenario:
        (scenario as 'PROPOSED' | 'CURRENT_HOLDINGS' | 'BENCHMARK') ||
        'PROPOSED',
      title: title || 'Forward Returns Breakdown',
      horizonDays,
      portfolioId: portfolioId || undefined,
      portfolioName: portfolioName || undefined,
    });
  }, [snapshotId, scenario, title, horizonDays, portfolioId, portfolioName]);

  // Export to PDF
  const handleExport = useCallback(async () => {
    if (!widgetRef.current || !data) {
      console.warn('Cannot export: widget element or data not available');
      return;
    }

    setIsExporting(true);
    try {
      await exportWidgetToPDF({
        widgetElement: widgetRef.current,
        title,
        portfolioName,
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  }, [widgetRef, data, title, portfolioName]);

  // Sort assets by return magnitude - smallest in center, largest on edges
  // Positive above baseline, negative below baseline
  const sortedAssets = useMemo(() => {
    if (!data?.constituents) return [];

    // DEBUG: Check if sum of final returns matches portfolio expected return
    const sumOfFinalReturns = data.constituents.reduce((sum, c) => {
      const finalReturn =
        c.cumulative_returns[c.cumulative_returns.length - 1] || 0;
      return sum + finalReturn;
    }, 0);
    console.log('DEBUG Portfolio Returns:', {
      portfolioExpectedAnnualReturn: data.portfolio_expected_annual_return,
      portfolioExpectedAnnualReturnPercent:
        (data.portfolio_expected_annual_return * 100).toFixed(2) + '%',
      sumOfFinalReturns,
      sumOfFinalReturnsPercent: (sumOfFinalReturns * 100).toFixed(2) + '%',
      difference:
        (
          (sumOfFinalReturns - data.portfolio_expected_annual_return) *
          100
        ).toFixed(2) + '%',
      numConstituents: data.constituents.length,
      constituentsBreakdown: data.constituents.map((c) => ({
        ticker: c.ticker || c.isin,
        weight: c.weight,
        finalReturn: c.cumulative_returns[c.cumulative_returns.length - 1],
        finalReturnPercent:
          (
            (c.cumulative_returns[c.cumulative_returns.length - 1] || 0) * 100
          ).toFixed(2) + '%',
      })),
    });

    // Calculate total return for each asset (last value in cumulative returns, convert to percentage)
    const assetsWithTotalReturn = data.constituents.map((constituent) => {
      const totalReturn =
        (constituent.cumulative_returns[
          constituent.cumulative_returns.length - 1
        ] || 0) * 100;
      return { ...constituent, totalReturn };
    });

    // Separate positive and negative returns
    const positiveAssets = assetsWithTotalReturn
      .filter((a) => a.totalReturn >= 0)
      .sort((a, b) => Math.abs(a.totalReturn) - Math.abs(b.totalReturn));

    const negativeAssets = assetsWithTotalReturn
      .filter((a) => a.totalReturn < 0)
      .sort((a, b) => Math.abs(b.totalReturn) - Math.abs(a.totalReturn));

    // Build the stack: largest negative, ..., smallest negative, smallest positive, ..., largest positive
    return [...negativeAssets.reverse(), ...positiveAssets];
  }, [data?.constituents]);

  // Transform data for cumulative area chart
  // Split each asset into positive and negative series to avoid sign-crossing visual artifacts
  const { chartData, seriesKeys } = useMemo(() => {
    if (!sortedAssets.length || !sortedAssets[0]?.dates) {
      return { chartData: [], seriesKeys: [] };
    }

    // Use dates from the first constituent (all should have same dates)
    const dates = sortedAssets[0].dates;

    // Track which series have non-zero values
    const hasPositive: Record<string, boolean> = {};
    const hasNegative: Record<string, boolean> = {};

    const data = dates.map((date, dateIndex) => {
      const dataPoint: Record<string, any> = {
        date: new Date(date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        fullDate: date,
      };

      let totalReturn = 0;

      sortedAssets.forEach((asset) => {
        const key = asset.ticker || asset.isin;
        const returnValue = (asset.cumulative_returns[dateIndex] || 0) * 100;

        // Positive series: clamp negatives to 0
        const posValue = Math.max(0, returnValue);
        dataPoint[`${key}_pos`] = posValue;
        if (posValue > 0) hasPositive[key] = true;

        // Negative series: clamp positives to 0
        const negValue = Math.min(0, returnValue);
        dataPoint[`${key}_neg`] = negValue;
        if (negValue < 0) hasNegative[key] = true;

        totalReturn += returnValue;
      });

      dataPoint.totalReturn = totalReturn;
      return dataPoint;
    });

    // Build series keys - only include series that have non-zero values
    const keys: { key: string; assetKey: string; type: 'pos' | 'neg' }[] = [];
    sortedAssets.forEach((asset) => {
      const assetKey = asset.ticker || asset.isin;
      if (hasNegative[assetKey]) {
        keys.push({ key: `${assetKey}_neg`, assetKey, type: 'neg' });
      }
      if (hasPositive[assetKey]) {
        keys.push({ key: `${assetKey}_pos`, assetKey, type: 'pos' });
      }
    });

    return { chartData: data, seriesKeys: keys };
  }, [sortedAssets]);

  // Generate colors for each asset based on P&L contribution
  // Green for positive, red for negative, yellow in between
  const assetColors = useMemo(() => {
    const colors: Record<string, string> = {};

    // Helper function to interpolate between colors
    const getColorForContribution = (
      finalReturn: number,
      maxAbsReturn: number,
    ) => {
      if (maxAbsReturn === 0) {
        // Get theme-aware gray color
        const computedStyle = getComputedStyle(document.documentElement);
        return (
          computedStyle.getPropertyValue('--bq-text-secondary').trim() ||
          '#6B7280'
        );
      }

      // Normalize the return to -1 to 1 range
      const normalized = finalReturn / maxAbsReturn;

      if (normalized > 0) {
        // Positive: interpolate from yellow (#FBBF24) to green (#10B981)
        const ratio = Math.abs(normalized);
        const r = Math.round(251 - (251 - 16) * ratio);
        const g = Math.round(191 + (185 - 191) * ratio);
        const b = Math.round(36 + (129 - 36) * ratio);
        return `rgb(${r}, ${g}, ${b})`;
      } else {
        // Negative: interpolate from yellow (#FBBF24) to red (#EF4444)
        const ratio = Math.abs(normalized);
        const r = Math.round(251 - (251 - 239) * ratio);
        const g = Math.round(191 - (191 - 68) * ratio);
        const b = Math.round(36 - (36 - 68) * ratio);
        return `rgb(${r}, ${g}, ${b})`;
      }
    };

    // Find max absolute return for normalization
    const maxAbsReturn = Math.max(
      ...sortedAssets.map((asset) => Math.abs(asset.totalReturn || 0)),
    );

    sortedAssets.forEach((asset) => {
      const key = asset.ticker || asset.isin;
      const finalReturn = asset.totalReturn || 0;
      colors[key] = getColorForContribution(finalReturn, maxAbsReturn);
    });

    return colors;
  }, [sortedAssets]);

  // Calculate Y-axis ticks and domain using fixed percentage intervals
  const yAxisConfig = useMemo(() => {
    if (!chartData.length) return { ticks: undefined, domain: undefined };

    // For stacked charts, we need to find the total stacked values (positive sum and negative sum)
    const stackedValues: number[] = [];
    chartData.forEach((dataPoint) => {
      let positiveSum = 0;
      let negativeSum = 0;
      seriesKeys.forEach(({ key }) => {
        const value = dataPoint[key] as number;
        if (value > 0) positiveSum += value;
        if (value < 0) negativeSum += value;
      });
      stackedValues.push(positiveSum, negativeSum);
    });

    if (stackedValues.length === 0)
      return { ticks: undefined, domain: undefined };

    const minValue = Math.min(...stackedValues);
    const maxValue = Math.max(...stackedValues);

    // Define available tick values with wider range for stacked charts
    const availableTicks = [
      -50, -40, -30, -20, -15, -10, -7.5, -5, -2.5, 0, 2.5, 5, 7.5, 10, 15, 20,
      30, 40, 50,
    ];

    // Select ticks that cover the data range with minimal padding
    const selectedTicks = availableTicks.filter(
      (tick) =>
        tick >= Math.floor(minValue) - 1 && tick <= Math.ceil(maxValue) + 1,
    );

    // Ensure we have at least 3 ticks and always include 0
    if (!selectedTicks.includes(0)) {
      selectedTicks.push(0);
      selectedTicks.sort((a, b) => a - b);
    }

    // Set domain to first and last tick
    const domain = [
      selectedTicks[0],
      selectedTicks[selectedTicks.length - 1],
    ] as [number, number];

    return { ticks: selectedTicks, domain };
  }, [chartData, seriesKeys]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={data ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (!data || !data.constituents || data.constituents.length === 0) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No asset breakdown data available."
          subTitle="Run portfolio analysis to generate asset breakdown"
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div
      className="hover-container h-full flex flex-column"
      id={widgetId}
      ref={widgetRef}
    >
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        onExport={handleExport}
        isExporting={isExporting}
        widgetId={widgetId}
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-visible">
        <div className="p-3 h-full">
          <Card
            className="h-full"
            pt={{
              content: {
                className: 'p-0 m-0 h-full flex flex-column',
              },
              body: {
                className: 'h-full',
              },
            }}
          >
            {/* Portfolio Info */}
            <div
              className="pb-2 border-bottom-1 mb-2 flex-shrink-0"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div
                className="text-sm"
                style={{ color: 'var(--bq-text-tertiary)' }}
              >
                Period Return:{' '}
                {(data.portfolio_expected_period_return * 100).toFixed(2)}% |
                Annual Return:{' '}
                {(data.portfolio_expected_annual_return * 100).toFixed(2)}% |
                Vol: {(data.portfolio_projected_volatility * 100).toFixed(2)}%
              </div>
            </div>

            {/* Chart */}
            <div
              ref={chartRef}
              className="flex-1 min-h-0"
              style={{ minHeight: '200px' }}
              onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--bq-border-muted)"
                  />
                  <XAxis
                    dataKey="date"
                    stroke="var(--bq-text-tertiary)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="var(--bq-text-tertiary)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value.toFixed(1)}%`}
                    ticks={yAxisConfig.ticks}
                    domain={yAxisConfig.domain}
                  />
                  <Tooltip
                    cursor={{
                      stroke: 'var(--bq-border-muted)',
                      strokeWidth: 1,
                    }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }

                      // Get the correct document body (works in popouts)
                      const targetBody =
                        chartRef.current?.ownerDocument?.body ?? document.body;

                      // Render tooltip content via portal to escape overflow containers
                      const renderTooltipPortal = (
                        content: React.ReactNode,
                        width = 280,
                      ) => {
                        // Get viewport dimensions from the correct window
                        const targetWindow =
                          chartRef.current?.ownerDocument?.defaultView ??
                          window;
                        const viewportWidth = targetWindow.innerWidth;
                        const viewportHeight = targetWindow.innerHeight;
                        let x = mousePos.x + 15;
                        let y = mousePos.y + 15;

                        // If tooltip would go off right edge, flip to left of cursor
                        if (x + width > viewportWidth - 10) {
                          x = mousePos.x - width - 15;
                        }
                        // If tooltip would go off bottom, flip above cursor
                        if (y + 300 > viewportHeight) {
                          y = Math.max(10, viewportHeight - 310);
                        }

                        return createPortal(
                          <div
                            style={{
                              position: 'fixed',
                              left: x,
                              top: y,
                              zIndex: 9999,
                              pointerEvents: 'none',
                            }}
                          >
                            {content}
                          </div>,
                          targetBody,
                        );
                      };

                      // If a specific asset is hovered, show only that asset's details
                      if (hoveredAsset) {
                        const asset = sortedAssets.find(
                          (a) => (a.ticker || a.isin) === hoveredAsset,
                        );

                        if (asset) {
                          // Find both pos and neg series and sum them
                          const posItem = payload.find(
                            (item) => item.dataKey === `${hoveredAsset}_pos`,
                          );
                          const negItem = payload.find(
                            (item) => item.dataKey === `${hoveredAsset}_neg`,
                          );
                          const currentReturn =
                            ((posItem?.value as number) || 0) +
                            ((negItem?.value as number) || 0);
                          const finalReturn = asset.totalReturn || 0;
                          const weight = asset.allocation_percent || 0;

                          return renderTooltipPortal(
                            <div
                              style={{
                                backgroundColor: 'var(--bq-surface-elevated)',
                                border: '1px solid var(--bq-border-muted)',
                                borderRadius: '6px',
                                padding: '12px',
                                width: '220px',
                              }}
                            >
                              <div style={{ marginBottom: '8px' }}>
                                <div
                                  style={{
                                    fontWeight: 'bold',
                                    color: 'var(--bq-text-primary)',
                                    fontSize: '14px',
                                  }}
                                >
                                  {asset.ticker || hoveredAsset}
                                </div>
                                {asset.name && (
                                  <div
                                    style={{
                                      color: 'var(--bq-text-secondary)',
                                      fontSize: '12px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={asset.name}
                                  >
                                    {asset.name}
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: 'var(--bq-text-primary)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                }}
                              >
                                <div>
                                  <span
                                    style={{
                                      color: 'var(--bq-text-secondary)',
                                    }}
                                  >
                                    Date:
                                  </span>{' '}
                                  {label}
                                </div>
                                <div>
                                  <span
                                    style={{
                                      color: 'var(--bq-text-secondary)',
                                    }}
                                  >
                                    Return at point:
                                  </span>{' '}
                                  <span
                                    style={{
                                      color:
                                        currentReturn > 0
                                          ? 'var(--bq-color-positive)'
                                          : currentReturn < 0
                                            ? 'var(--bq-color-negative)'
                                            : 'var(--bq-text-secondary)',
                                    }}
                                  >
                                    {currentReturn.toFixed(2)}%
                                  </span>
                                </div>
                                <div>
                                  <span
                                    style={{
                                      color: 'var(--bq-text-secondary)',
                                    }}
                                  >
                                    Final return:
                                  </span>{' '}
                                  <span
                                    style={{
                                      color:
                                        finalReturn > 0
                                          ? 'var(--bq-color-positive)'
                                          : finalReturn < 0
                                            ? 'var(--bq-color-negative)'
                                            : 'var(--bq-text-secondary)',
                                    }}
                                  >
                                    {finalReturn.toFixed(2)}%
                                  </span>
                                </div>
                                <div>
                                  <span
                                    style={{
                                      color: 'var(--bq-text-secondary)',
                                    }}
                                  >
                                    Weight:
                                  </span>{' '}
                                  {weight.toFixed(2)}%
                                </div>
                              </div>
                            </div>,
                            220,
                          );
                        }
                      }

                      // No specific asset hovered - show full table of all returns
                      const totalReturnItem = payload.find(
                        (item) => item.dataKey === 'totalReturn',
                      );
                      const totalReturn =
                        (totalReturnItem?.value as number) || 0;

                      // Get all asset returns, combining pos/neg series back into single assets
                      // sorted by current return (highest to lowest)
                      const assetReturnMap: Record<
                        string,
                        {
                          ticker: string;
                          name: string;
                          value: number;
                          color: string;
                        }
                      > = {};

                      payload
                        .filter((item) => item.dataKey !== 'totalReturn')
                        .forEach((item) => {
                          const dataKey = item.dataKey as string;
                          // Extract asset key by removing _pos or _neg suffix
                          const assetKey = dataKey.replace(/_pos$|_neg$/, '');
                          const asset = sortedAssets.find(
                            (a) => (a.ticker || a.isin) === assetKey,
                          );

                          if (!assetReturnMap[assetKey]) {
                            assetReturnMap[assetKey] = {
                              ticker: assetKey,
                              name: asset?.name || '',
                              value: 0,
                              color: item.color as string,
                            };
                          }
                          // Sum pos and neg values to get actual return
                          assetReturnMap[assetKey].value +=
                            (item.value as number) || 0;
                        });

                      const assetReturns = Object.values(assetReturnMap).sort(
                        (a, b) => b.value - a.value,
                      );

                      return renderTooltipPortal(
                        <div
                          style={{
                            backgroundColor: 'var(--bq-surface-elevated)',
                            border: '1px solid var(--bq-border-muted)',
                            borderRadius: '4px',
                            padding: '6px 8px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '4px',
                              paddingBottom: '4px',
                              borderBottom: '1px solid var(--bq-border-muted)',
                              fontSize: '11px',
                            }}
                          >
                            <span
                              style={{
                                color: 'var(--bq-text-primary)',
                                fontWeight: 600,
                              }}
                            >
                              {label}
                            </span>
                            <span
                              style={{
                                color:
                                  totalReturn >= 0
                                    ? 'var(--bq-color-positive)'
                                    : 'var(--bq-color-negative)',
                                fontWeight: 600,
                              }}
                            >
                              {totalReturn >= 0 ? '+' : ''}
                              {totalReturn.toFixed(2)}%
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '1px 12px',
                              fontSize: '10px',
                            }}
                          >
                            {assetReturns.map((asset) => (
                              <div
                                key={asset.ticker}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '1px',
                                      backgroundColor: asset.color,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      color: 'var(--bq-text-primary)',
                                      fontWeight: 500,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {asset.ticker}
                                  </span>
                                  {asset.name && (
                                    <span
                                      style={{
                                        color: 'var(--bq-text-secondary)',
                                        maxWidth: '50px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={asset.name}
                                    >
                                      {asset.name}
                                    </span>
                                  )}
                                </div>
                                <span
                                  style={{
                                    color:
                                      asset.value >= 0
                                        ? 'var(--bq-color-positive)'
                                        : 'var(--bq-color-negative)',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {asset.value >= 0 ? '+' : ''}
                                  {asset.value.toFixed(1)}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>,
                        280,
                      );
                    }}
                  />

                  {/* Render area for each asset series (split into pos/neg) */}
                  {seriesKeys.map(({ key, assetKey, type }) => {
                    const isHovered = hoveredAsset === assetKey;
                    const isSomethingHovered = hoveredAsset !== null;

                    return (
                      <Area
                        key={key}
                        type="linear"
                        dataKey={key}
                        stackId={type}
                        stroke={isHovered ? 'none' : assetColors[assetKey]}
                        strokeWidth={isHovered ? 0 : 0.5}
                        fill={assetColors[assetKey]}
                        fillOpacity={
                          isHovered ? 1 : isSomethingHovered ? 0.3 : 0.7
                        }
                        isAnimationActive={false}
                        dot={false}
                        activeDot={false}
                        onMouseEnter={() => handleMouseEnter(assetKey)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })}

                  {/* Overall portfolio return line */}
                  <Line
                    type="monotone"
                    dataKey="totalReturn"
                    stroke="var(--bq-text-primary)"
                    strokeWidth={2}
                    dot={false}
                    name="Total Portfolio Return"
                    activeDot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Asset Summary */}
            <div
              className="mt-2 pt-2 border-top-1 flex-shrink-0"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div
                className="text-xs mb-2"
                style={{ color: 'var(--bq-text-tertiary)' }}
              >
                Showing {sortedAssets.length} assets • Cumulative returns by
                asset
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
